import type { ComponentType } from 'react'
import type { LoaderFunction, RouteObject } from 'react-router'

// Define the shape of the module we expect our route files to export.
// This ensures type safety for our helper.
interface RouteModule {
  // React Router instantiates routes with no explicit props, so the default ComponentType is appropriate.
  default: ComponentType
  loader?: LoaderFunction
  // You could also add `action?: ActionFunction` here if you use actions.
}

// Define a type for the optional route properties we can pass in.
// We Omit the properties that our helper function sets itself.
type LazyRouteOptions = Omit<RouteObject, 'path' | 'lazy'>
type LazyIndexRouteOptions = Omit<RouteObject, 'index' | 'lazy' | 'children'>

/**
 * Creates a lazy-loaded route object for React Router.
 * @param path - The URL path for the route.
 * @param importFn - A function that returns a dynamic import for the route's module.
 * Example: () => import('./pages/DashboardPage')
 * @returns A RouteObject with a lazy-loaded component and loader.
 */
export function lazyRoute(
  path: string,
  importFn: () => Promise<RouteModule>,
  options: LazyRouteOptions = {},
): RouteObject {
  return {
    ...options,
    index: false,
    path,
    async lazy() {
      // 1. Wait for the dynamic import to resolve
      const { default: Component, loader } = await importFn()

      // 2. Return the component and loader for React Router to use
      return {
        Component,
        loader,
      }
    },
  }
}

/**
 * Creates a lazy-loaded index route object for React Router.
 * @param importFn - A function that returns a dynamic import for the route's module.
 * @returns An index RouteObject with a lazy-loaded component and loader.
 */
export function lazyIndexRoute(importFn: () => Promise<RouteModule>, options: LazyIndexRouteOptions = {}): RouteObject {
  return {
    ...options,
    index: true,
    async lazy() {
      const { default: Component, loader } = await importFn()
      return {
        Component,
        loader,
      }
    },
  }
}
